import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { useVisibleFieldsByProcess } from 'store/visibleFields';
import useDebounce from 'hooks/useDebounce';
import { useMemo } from 'react';
import { logError } from 'setup/logger/logError';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { isProcessValue } from 'shared/domain/process/types/model';
import { getStaticFieldsOnProcess } from 'shared/domain/visibleField/availableFields';
import {
  buildReportPageEstimateInput,
  getReportPageEstimateFormDependencyKey,
} from 'shared/domain/report/estimate/buildReportPageEstimateInput';
import { estimateReportPdfFileCount } from 'shared/domain/report/estimate/estimateReportPdfFileCount';
import {
  estimateReportPages,
  formatEstimatedPageRange,
} from 'shared/domain/report/estimate/reportPageEstimate';
import {
  buildInspectionPageEstimateInput,
  estimateInspectionPages,
  getInspectionPageEstimateFormDependencyKey,
} from 'shared/domain/report/estimate/inspection';
import { getAvailableFieldsForProcess } from 'shared/domain/report/estimate/reportIssueEstimateRow';
import {
  IssueReportType,
  ReportType,
} from 'shared/domain/report/types/model/common';
import { isIssueReportOnView } from 'shared/domain/report/types/onView/issueReportOnViewGuards';
import { isSingleInspectionReportOnView } from 'shared/domain/report/types/onView/singleInspectionReportOnViewGuards';
import {
  isInspectionReportType,
  isIssueReportType,
  isSingleIssueReportType,
} from 'shared/domain/report/types/model/reportTypeGuards';
import { useReportPageEstimateContext } from '../contexts/ReportPageEstimateContext';

const ESTIMATE_FORM_DEBOUNCE_MS = 200;

function createAvailableFieldsResolver(
  visibleFieldsByProcess: ReturnType<typeof useVisibleFieldsByProcess>
): (processId: string) => Set<IssueFieldNames> {
  const staticFieldsByProcess = new Map<string, IssueFieldNames[]>();

  return (processId: string): Set<IssueFieldNames> => {
    if (!staticFieldsByProcess.has(processId)) {
      staticFieldsByProcess.set(
        processId,
        (isProcessValue(processId)
          ? getStaticFieldsOnProcess(processId)
          : []
        ).map((field) => field.fieldName)
      );
    }

    return getAvailableFieldsForProcess(
      processId,
      visibleFieldsByProcess,
      staticFieldsByProcess.get(processId) ?? []
    );
  };
}

export function useReportPageEstimate(): {
  pages: number;
  pageRange: string;
  fileCount: number;
  progressPercent: number;
  isLoading: boolean;
  isIssueReport: boolean;
} {
  const { values } = useInputForm();
  const {
    issueCount,
    issueRows,
    isLoading: isSourceLoading,
    kind,
    inspectionSource,
  } = useReportPageEstimateContext();
  const visibleFields = useVisibleFieldsByProcess();

  const getAvailableFieldsForProcessFn = useMemo(
    () => createAvailableFieldsResolver(visibleFields),
    [visibleFields]
  );

  const isIssueReport = isIssueReportType(kind);
  const isInspectionReport = isInspectionReportType(kind);
  const inspectionValues = isSingleInspectionReportOnView(values)
    ? values
    : null;

  const estimateFormKey = isInspectionReport && inspectionValues
    ? getInspectionPageEstimateFormDependencyKey(inspectionValues)
    : isIssueReportOnView(values)
      ? getReportPageEstimateFormDependencyKey(values)
      : '';
  const debouncedEstimateFormKey = useDebounce(
    estimateFormKey,
    ESTIMATE_FORM_DEBOUNCE_MS
  );

  return useMemo(() => {
    if (isInspectionReport) {
      const inspectionFormValues = isSingleInspectionReportOnView(values)
        ? values
        : null;
      const fileCount = 1;

      if (!inspectionFormValues) {
        const pages = 1;
        return {
          pages,
          pageRange: formatEstimatedPageRange(pages),
          fileCount: Math.max(1, fileCount),
          progressPercent: 5,
          isLoading: false,
          isIssueReport: false,
        };
      }

      if (isSourceLoading || !inspectionSource) {
        return {
          pages: 0,
          pageRange: '…',
          fileCount: Math.max(1, fileCount),
          progressPercent: 0,
          isLoading: true,
          isIssueReport: false,
        };
      }

      const input = buildInspectionPageEstimateInput(
        inspectionFormValues,
        inspectionSource,
        getAvailableFieldsForProcessFn
      );

      try {
        const { pages, progressPercent } = estimateInspectionPages(input);

        return {
          pages,
          pageRange: formatEstimatedPageRange(pages),
          fileCount: Math.max(1, fileCount),
          progressPercent,
          isLoading: false,
          isIssueReport: false,
        };
      } catch (error) {
        logError('Inspection report page estimate failed', error);
        const pages = 1;
        return {
          pages,
          pageRange: formatEstimatedPageRange(pages),
          fileCount: Math.max(1, fileCount),
          progressPercent: 5,
          isLoading: false,
          isIssueReport: false,
        };
      }
    }

    if (!isIssueReport) {
      const pages = 1;
      return {
        pages,
        pageRange: formatEstimatedPageRange(pages),
        fileCount: 1,
        progressPercent: 5,
        isLoading: false,
        isIssueReport: false,
      };
    }

    const effectiveIssueCount = Math.max(issueCount, issueRows.length, 0);
    const fileCount = estimateReportPdfFileCount(effectiveIssueCount);

    if (isSourceLoading && issueRows.length === 0) {
      return {
        pages: 0,
        pageRange: '…',
        fileCount: 1,
        progressPercent: 0,
        isLoading: true,
        isIssueReport: true,
      };
    }

    const reportKind: IssueReportType = isSingleIssueReportType(kind)
      ? ReportType.singleIssue
      : ReportType.issueList;

    if (!isIssueReportOnView(values)) {
      const pages = 1;
      return {
        pages,
        pageRange: formatEstimatedPageRange(pages),
        fileCount,
        progressPercent: 5,
        isLoading: false,
        isIssueReport: true,
      };
    }

    const input = buildReportPageEstimateInput(
      values,
      issueRows,
      reportKind,
      getAvailableFieldsForProcessFn
    );
    const { pages, progressPercent } = estimateReportPages(input);

    return {
      pages,
      pageRange: formatEstimatedPageRange(pages),
      fileCount,
      progressPercent,
      isLoading: false,
      isIssueReport: true,
    };
  }, [
    debouncedEstimateFormKey,
    getAvailableFieldsForProcessFn,
    inspectionSource,
    isInspectionReport,
    isIssueReport,
    isSourceLoading,
    issueCount,
    issueRows,
    kind,
  ]);
}
